import { request, SKIP_AUTH_HEADER } from '../../api'
import { useServerFetch } from '../../composables/useServerFetch'
import { normalizeCountryCodeValue } from '../../stores/app'

export type TraderLabel = {
  code?: string
  name?: string
  color?: string
  type?: number
}

export type TraderLabelGroup = {
  title?: string
  items?: TraderLabel[] | null
}

export type TraderRiskItem = {
  level?: number
  image?: string
  content?: string
  category?: number
}

export type TraderRisk = {
  icon?: string
  title?: string
  count?: number
  desc?: string
  borderColor?: string
  backgroundColor?: string
  items?: TraderRiskItem[] | null
}

export type TraderBasicInfoValueItem = {
  label?: string
  value?: string
  icon?: string
}

export type TraderBasicInfoContact = {
  type?: number
  label?: string
  value?: string
  expand?: string
  icon?: string
  isSocial?: boolean
}

export type TraderBasicInfoDisplayItem = {
  key?: string
  label?: string
  value?: string
  icon?: string
  type?: number
}

export type TraderBasicInfo = {
  traderCode?: string
  traderType?: number
  showName?: string
  shortName?: string
  fullName?: string
  localShortName?: string
  localFullName?: string
  shortNameLocal?: string
  logo?: string
  ico?: string
  topIco?: string
  flag?: string
  country?: string
  countryName?: string
  status?: number
  statusPosition?: number
  annotation?: string
  annotationColor?: string
  originColor?: string
  institutionLicensceColor?: string
  isWhite?: boolean
  isSmallWhite?: boolean
  isadvanced?: boolean
  seal?: string
  top?: string
  channel?: number
  matrix?: number
  labels?: TraderLabel[] | null
  sortedLabels?: TraderLabelGroup[] | null
  registeredRegion?: TraderBasicInfoValueItem | null
  operatingYears?: string
  companyName?: string
  exchangeShortName?: string
  exchangeType?: string
  contactPhone?: string
  website?: string
  twitter?: string
  instagram?: string
  facebook?: string
  whatsapp?: string
  contacts?: TraderBasicInfoContact[] | null
  displayItems?: TraderBasicInfoDisplayItem[] | null
  [key: string]: unknown
}

export type TraderTopLabel = {
  type?: number
  ico?: string
  icoWidth?: string
  icoHeight?: string
  text?: string
  borderColor?: string
  backgroundColor?: string
  fontColor?: string
  backgroundGif?: string
}

export type TraderBusinessPopupTips = {
  text?: string
  color?: string
}

export type TraderBusinessPopupRegulator = {
  icon?: string
  text?: string
}

export type TraderBusinessPopup = {
  title?: string
  tips?: TraderBusinessPopupTips | null
  notes?: string
  regulators?: TraderBusinessPopupRegulator[] | null
  supportDocs?: string[] | null
}

export type TraderBusinessLabel = {
  linkType?: number
  labelType?: number
  content?: string
  borderColor?: string
  backgroundColor?: string
  fontColor?: string
  businessPopup?: TraderBusinessPopup | null
}

export type TraderRatingCard = {
  value?: string | number
  time?: string
  tips?: string
}

export type TraderDepositWithdrawalItem = {
  title?: string
  content?: string
  flag?: string
}

export type TraderDepositWithdrawalCard = {
  title?: string
  titleType?: number
  items?: TraderDepositWithdrawalItem[] | null
}

export type TraderDepositWithdrawalFiat = {
  name?: string
  code?: string
  paymentMethod?: string
  minAmount?: string
  flag?: string
}

export type TraderDepositWithdrawalListItem = {
  tradeType?: string
  fiats?: TraderDepositWithdrawalFiat[] | null
}

export type TraderDepositWithdrawalDescItem = {
  title?: string
  desc?: string
}

export type TraderDepositWithdrawalDesc = {
  title?: string
  list?: TraderDepositWithdrawalDescItem[] | null
}

export type TraderDepositWithdrawalTab = {
  title?: string
  titleType?: number
  type?: number
  hasMore?: boolean
  currencyCode?: string
  cards?: TraderDepositWithdrawalCard[] | null
  list?: TraderDepositWithdrawalListItem[] | null
  desc?: TraderDepositWithdrawalDesc | null
}

export type TraderDepositAndWithdrawal = {
  topicTitle?: string
  tabs?: TraderDepositWithdrawalTab[] | null
}

export type TraderDepositAndWithdrawalSection = {
  title?: string
  type?: number
  tabs?: TraderDepositWithdrawalTab[] | null
}

export type TraderSurveyItem = {
  surveyId?: string
  sid?: string
  urlPath?: string
  isexhibition?: boolean
  code?: string
  traderName?: string
  evaluate?: string
  evaluateColor?: string
  riskType?: number
  level?: number
  date?: string
  surveyDate?: string
  wikiTimestamp?: number
  surveyTimestamp?: number
  cover?: string
  images?: string[] | null
  ico?: string
  flag?: string
  country?: string
  city?: string
  address?: string
  title?: string
  summary?: string
  videoUrl?: string
  url?: string
  vrIcon?: string
  labels?: Array<{
    type?: number
    text?: string
    borderColor?: string
    backgroundColor?: string
    fontColor?: string
  }> | null
  detail?: {
    sid?: string
    urlPath?: string
    isexhibition?: boolean
    code?: string
    title?: string
    summary?: string
    content?: string
    country?: string
    address?: string
    date?: string
    wikiTimestamp?: number
    type?: number
    level?: number
    evaluate?: string
    evaluateCode?: string
    cover?: string
    images?: string[] | null
    share?: string
    communityShare?: string
    googleAddr?: string
    baiduAddr?: string
    baiduImg?: string
    googleImg?: string
    googleStaticPic?: string
    bdStaticPic?: string
    mapStaticPic?: string
    mapStaticPics?: string[] | null
    streetViewPic?: string
    aggInfo?: {
      code?: string
      countryCode?: string
    }
  } | null
}

export type TraderSurvey = {
  title?: string
  topicTitle?: string
  total?: number
  items?: TraderSurveyItem[] | null
  flags?: string[] | null
}

export type TraderOverviewTag = {
  text?: string
  title?: string
  icon?: string
  fontColor?: string
  backgroundColor?: string
  borderColor?: string
  desc?: string
  type?: number
}

export type TraderOverviewTagSection = {
  title?: string
  tags?: TraderOverviewTag[] | null
}

export type TraderExchangeOverview = {
  title?: string
  topicTitle?: string
  introTitle?: string
  introSubtitle?: string
  intro?: string
  paragraphs?: string[] | null
  companyImage?: TraderOverviewTagSection | null
  securityMeasures?: TraderOverviewTagSection | null
}

export type TraderSafetyMeasureSingleTag = {
  code?: number
  value?: string
  name?: string
  title?: string
  text?: string
  icon?: string
  description?: string
  desc?: string
}

export type TraderSafetyMeasureComboTag = {
  name?: string
  value?: string
  title?: string
  text?: string
  icon?: string
  description?: string
  desc?: string
}

export type TraderSafetyMeasuresList = {
  title?: string
  topicTitle?: string
  hasMore?: boolean
  singleMaxLines?: number
  comboMaxLines?: number
  singleTags?: Array<TraderSafetyMeasureSingleTag | string> | null
  comboTags?: TraderSafetyMeasureComboTag[] | null
}

export type TraderMarketStrategyTab = {
  code?: string
  title?: string
}

export type TraderMarketStrategyAreaDetail = {
  name?: string
  level?: string
  levelIndex?: number
}

export type TraderMarketStrategyArea = {
  level?: string
  exceedPercentage?: string
  percentage?: string
  details?: TraderMarketStrategyAreaDetail[] | null
  color?: string
}

export type TraderMarketInfluenceItem = {
  countryCode?: string
  countryName?: string
  countryFlag?: string
  index?: string
  isUserCountry?: boolean
  color?: string
  isOther?: boolean
}

export type TraderMarketStrategyInfluence = {
  country?: TraderMarketInfluenceItem[] | null
  company?: TraderMarketInfluenceItem[] | null
  level?: string
}

export type TraderMarketStrategyData = {
  tabs?: TraderMarketStrategyTab[] | null
  traderCode?: string
  hasInfluence?: boolean
  hasMarketStrategy?: boolean
  updateDate?: string
  image?: string
  area?: TraderMarketStrategyArea | null
  influence?: TraderMarketStrategyInfluence | null
}

export type TraderMarketStrategy = {
  title?: string
  chartTitle?: string
  data?: TraderMarketStrategyData | null
}

export type LicenseDetailTextIcon = {
  width?: number
  height?: number
  iconUrl?: string
  corner?: number
}

export type LicenseDetailText = {
  size?: number
  weight?: number
  maxLines?: number | null
  color?: string
  bgColor?: string
  content?: string
  prefixIcon?: LicenseDetailTextIcon | null
  suffixIcon?: LicenseDetailTextIcon | null
  isAllowCopy?: boolean
}

export type LicenseDetailSection = {
  type?: number
  type1Content?: {
    label?: LicenseDetailText
    value?: LicenseDetailText
  } | null
  type2Content?: {
    label?: LicenseDetailText
    value?: LicenseDetailText[] | null
  } | null
  type4Content?: {
    stamp?: LicenseDetailTextIcon[] | null
  } | null
  type5Content?: {
    label?: LicenseDetailText
    items?: Array<{
      replaceName?: string
      name?: LicenseDetailText
      tap?: {
        type?: number
        targetId?: string
        extendId?: string
      } | null
      popUp?: unknown
    }> | null
  } | null
}

export type LicenseDetail = {
  bgImage?: string[] | null
  header?: {
    logo?: string
    name?: string
    serialNumber?: string
    licenseName?: string
    levelIcon?: string
  } | null
  sections?: LicenseDetailSection[] | null
  bottom?: {
    files?: string[] | null
    updateTime?: string
  } | null
}

export type FeeRate = {
  tabs?: FeeRateTab[] // 一级 tab 列表
  table?: FeeRateTable // 表格数据
}

export type FeeRateSubTab = {
  title?: string // 二级 tab 标题
  code?: string // 二级 tab code
}

export type FeeRateTab = {
  title?: string // 一级 tab 标题
  code?: string // 一级 tab code
  subTabs?: FeeRateSubTab[] // 二级 tab
}

export type FeeRateTable = {
  header?: string[] // 表头
  data?: FeeRateTableRow[] // 表格数据
}

export type FeeRateTableRow = {
  icon?: string // 币种 icon
  code?: string // 币种 code
  name?: string // 币种名称
  networkName?: string // 网络名称
  networkFee?: string // 网络费用
  minAmount?: string // 最小数量
}

export type TraderOfficialTokenInfo = {
  title?: string
  content?: string
}

export type TraderOfficialTokenTab = {
  title?: string
  exchangeLogo?: string
  exchangeShortName?: string
  exchangeFullName?: string
  tokenPrice?: string
  tokenChange?: string
  changeType?: number
  infoMaxLines?: number
  infos?: TraderOfficialTokenInfo[] | null
}

export type TraderOfficialToken = {
  title?: string
  topicTitle?: string
  hasMore?: boolean
  tabs?: TraderOfficialTokenTab[] | null
}

export type TraderSupportCurrencyPair = {
  code?: string
  name?: string
  baseLogo?: string
  pairCount?: string
  price?: string
  change?: string
  changeType?: number
  volume24h?: string
  marketDepth?: string
  liquidity?: string
}

export type TraderSupportCurrency = {
  topicTitle?: string
  coinCount?: string
  pairCount?: string
  liquidityRank?: string
  headers?: string[] | null
  pairList?: TraderSupportCurrencyPair[] | null
}

export type TraderDetail = {
  basicInfo?: TraderBasicInfo | null
  assetStrength?: Record<string, unknown> | null
  traderTopLabels?: TraderTopLabel[] | null
  businessLabels?: TraderBusinessLabel[] | null
  emc?: Record<string, unknown> | null
  risk?: TraderRisk | null
  officialToken?: TraderOfficialToken | null
  supportCurrency?: TraderSupportCurrency | null
  bitDepositAndWithdrawal?: TraderDepositAndWithdrawal | TraderDepositAndWithdrawalSection[] | null
  safelyMeasuresList?: TraderSafetyMeasuresList | null
  survey?: TraderSurvey | null
  exchangeOverview?: TraderExchangeOverview | null
  ratingCards?: TraderRatingCard | null
  marketStrategy?: TraderMarketStrategy | null
  feeRate?: FeeRate | null
  [key: string]: unknown
}

type TraderDetailResponse = {
  code?: number
  data?: TraderDetail
  message?: string
  succeed?: boolean
}

type TraderDetailResult = TraderDetailResponse | TraderDetail | null
type WrappedApiResponse<T> = T | { code?: number; data?: T | null; message?: string; succeed?: boolean } | null

export type TraderFundFlowChartPoint = {
  flowType?: number
  label?: string
  displayValue?: string
  value?: number
  timestamp?: number
}

export type TraderTradingVolumeChartPoint = {
  displayValue?: string
  value?: number
  timestamp?: number
}

export type TraderAssetStrengthPointChart = {
  labels?: string[]
  points?: Array<number | string>
  pointsVal?: number[]
}

export type TraderAssetStrengthTrend = {
  title?: string
  score?: number
  scoreMax?: number
  amount?: string
  changePercent?: string
  changeDirection?: string
  badge?: string
  chart?: TraderAssetStrengthPointChart
}

export type TraderAssetStrengthChart = {
  fundFlow?: TraderAssetStrengthTrend
  tradingVolume?: TraderAssetStrengthTrend
}

const extractTraderDetail = (response: TraderDetailResult) => {
  if (!response || typeof response !== 'object') {
    return null
  }

  if ('data' in response) {
    return response.data || null
  }

  return response
}

const normalizeTraderDetail = (response: TraderDetailResult) => {
  const detail = extractTraderDetail(response) as TraderDetail | null

  if (!detail) {
    return null
  }

  return detail
}

const unwrapApiResponse = <T>(response: WrappedApiResponse<T>): T | null => {
  if (!response || typeof response !== 'object') {
    return response as T | null
  }

  if ('data' in response && ('code' in response || 'message' in response || 'succeed' in response)) {
    return response.data ?? null
  }

  return response as T
}

type GetTraderDetailParams = {
  brandCode?: string
  timeRangeType?: string | number
}

/** 获取交易所详情，并在已解析到品牌编码时透传给 BFF 读取品牌级配置。 */
export const getTraderDetail = async (traderCode: string, params: GetTraderDetailParams = {}) => {
  const api = useEnvApi()
  const requestParams: Record<string, string | number> = {
    traderCode,
  }

  const brandCode = params.brandCode?.trim()
  if (brandCode) {
    requestParams.brandCode = brandCode
  }

  if (params.timeRangeType !== undefined && params.timeRangeType !== '') {
    requestParams.timeRangeType = params.timeRangeType
  }

  const requestConfig = {
    baseURL: import.meta.server ? api.wikibitBffGo : api.v4,
    url: '/api/v4/bit/web/trader/detail',
    method: 'GET',
    params: requestParams,
  } as const

  const response = import.meta.server
    ? await useServerFetch<TraderDetailResult>(requestConfig)
    : ((await request<TraderDetailResult>(requestConfig)) as unknown as TraderDetailResult)

  return normalizeTraderDetail(response as TraderDetailResult)
}

export const getTraderAssetStrengthChart = async ({
  traderCode,
  timeRangeType,
  languageCode,
  countryCode = '156',
}: {
  traderCode: string
  timeRangeType: string | number
  languageCode?: string
  countryCode?: string
}): Promise<TraderAssetStrengthChart | null> => {
  const api = useEnvApi()
  const normalizedLanguageCode = (languageCode ?? '').trim().toLowerCase()
  const params = {
    traderCode,
    timeRangeType,
  }
  const config = {
    baseURL: api.v4,
    url: '/api/v4/bit/web/trader/detail/asset-strength/chart',
    method: 'GET' as const,
    params,
    headers: {
      basicdata1: '999,0,2,999,0,1389dee6828f3daa027d9528a374dfc6,0',
      BasicData: '999,0,2,999,0,1389dee6828f3daa027d9528a374dfc6,0',
      CountryCode: normalizeCountryCodeValue(countryCode) || '156',
      LanguageCode: normalizedLanguageCode || 'zh-cn',
    },
  }
  const response = await request<unknown, WrappedApiResponse<TraderAssetStrengthChart>>(config)

  return unwrapApiResponse(response)
}

export const getTraderFundFlowChart = async (
  traderCode: string,
  timeRangeType: string | number,
): Promise<TraderFundFlowChartPoint[]> => {
  const api = useEnvApi()
  const params = {
    exchangeId: traderCode,
    timeRangeType,
  }
  const config = {
    baseURL: api.wikibitBffGo,
    url: '/api/v4/bit/trader/detail/fund-flow/chart',
    method: 'GET' as const,
    params,
  }
  const response = import.meta.server
    ? await useServerFetch<WrappedApiResponse<TraderFundFlowChartPoint[]>>({
        ...config,
        query: params,
      })
    : ((await request<unknown, WrappedApiResponse<TraderFundFlowChartPoint[]>>(config)) as WrappedApiResponse<
        TraderFundFlowChartPoint[]
      >)
  const data = unwrapApiResponse(response)

  return Array.isArray(data) ? data : []
}

export const getTraderTradingVolumeChart = async (
  traderCode: string,
  timeRangeType: string | number,
): Promise<TraderTradingVolumeChartPoint[]> => {
  const api = useEnvApi()
  const params = {
    exchangeId: traderCode,
    timeRangeType,
    subTabType: 0,
  }
  const config = {
    baseURL: api.wikibitDatahubGo,
    url: '/api/v4/datahub/market-data/trade-volume/chart',
    method: 'GET' as const,
    params,
  }
  const response = import.meta.server
    ? await useServerFetch<WrappedApiResponse<TraderTradingVolumeChartPoint[]>>({
        ...config,
        query: params,
      })
    : ((await request<unknown, WrappedApiResponse<TraderTradingVolumeChartPoint[]>>(config)) as WrappedApiResponse<
        TraderTradingVolumeChartPoint[]
      >)
  const data = unwrapApiResponse(response)

  return Array.isArray(data) ? data : []
}

const isLicenseDetail = (value: unknown): value is LicenseDetail => Boolean(value && typeof value === 'object')

const normalizeLicenseDetail = (response: unknown): LicenseDetail | null => {
  if (!isLicenseDetail(response)) {
    return null
  }

  if ('data' in response) {
    return normalizeLicenseDetail(response.data)
  }

  return response
}

export const getLicenseDetail = async (
  projectCode: string,
  regulationId: string,
  childProjectCode = '',
): Promise<LicenseDetail | null> => {
  const api = useEnvApi()
  const params: Record<string, string | number> = {
    projectCode,
    regulationId,
    type: 0,
  }

  if (childProjectCode) {
    params.childProjectCode = childProjectCode
  }

  const requestConfig = {
    baseURL: import.meta.server ? api.wikibitBffGo : api.v4,
    url: '/api/v4/bit/web/license/detail',
    method: 'GET',
    params,
    headers: {
      [SKIP_AUTH_HEADER]: '1',
    },
  }
  const response = import.meta.server ? await useServerFetch(requestConfig) : await request(requestConfig)

  return normalizeLicenseDetail(response)
}

export const getLicenseChangeLog = async (regulationId: string): Promise<any> => {
  const api = useEnvApi()

  return await request({
    baseURL: api.homeClient,
    url: `${WIKIBITWEBV1}/invoke-v3/v3`,
    method: 'POST',
    data: {
      regulationId,
    },
    headers: {
      Routes: 'wikifxagg$regulatorregulationchangelogv2',
    },
  })
}

export const getLicenseRelatedRisk = (regulationId: string, traderCode: string): Promise<any> => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.wikibitBffGo : api.v4,
    url: '/api/v4/license/related-risk',
    method: 'GET',
    params: {
      regulationId,
      traderCode,
    },
  }

  return import.meta.server ? useServerFetch(requestConfig) : request(requestConfig)
}

export const getLicenseExposureFilterBrands = (regulationId: string): Promise<any> => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.wikibitBffGo : api.v4,
    url: '/api/v4/license/exposure/filter-brands',
    method: 'GET',
    params: {
      regulationId,
    },
  }

  return import.meta.server ? useServerFetch(requestConfig) : request(requestConfig)
}

export const getLicenseExposureInfoList = (params: {
  regulationId: string
  pageSize: number
  pageNum: number
  traderCode?: string
}): Promise<any> => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.wikibitBffGo : api.v4,
    url: '/api/v4/license/exposure-info/list',
    method: 'GET',
    params,
  }

  return import.meta.server ? useServerFetch(requestConfig) : request(requestConfig)
}

export const getGuaranteePop = (params: { traderCode: string }) => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.wikibitBffGo : api.v4,
    url: '/api/v4/bit/web/trader/detail/iap/guarantee-pop',
    method: 'GET',
    params,
  }

  return import.meta.server ? useServerFetch(requestConfig) : request(requestConfig)
}

export const getAliasResolve = (params: { alias: string }) => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.wikibitBffGo : api.v4,
    url: '/api/v4/bit/web/trader/alias-resolve',
    method: 'GET',
    params,
  }

  return import.meta.server ? useServerFetch(requestConfig) : request(requestConfig)
}
